import React, { useEffect, useState } from 'react';
import { element } from 'prop-types';
import useSWR from 'swr';

import { Modal, SimpleLoading } from '~/components';
import { useApi, useIdleLogout } from '~/hooks';
import { useAuth } from '~/services';
import { useTranslation } from 'react-i18next';
import { meContext } from './context';

const BE_LAST_LOGIN_KEY = 'dd-be-last-login';

/**
 * Valida se o group armazenado pertence à hierarquia do usuário.
 */
const isGroupInUserHierarchy = (group, userData) => {
  if (!group?.value || !group?.label || !userData?.hierarquia?.length) return false;
  return userData.hierarquia.some(
    ({ id, cliente }) => id === group.value && cliente === group.label
  );
};

/**
 * Provider de dados do usuário para o fluxo Becompliance → RiskCheck.
 * Busca /me na API v3 e fornece a mesma interface do MeProvider.
 * Usa dd-be-last-login separado para não misturar com dados do fluxo Keycloak.
 */
export function BeMeProvider({ children }) {
  const api = useApi();
  const { t } = useTranslation('components');
  const { isAuthenticated, logout } = useAuth();

  const [group, onGroup] = useState(() => {
    try {
      const stored = localStorage.getItem(BE_LAST_LOGIN_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const fetcher = async (url) => {
    const response = await api().get(url);
    return response.data;
  };

  const { data: user, isValidating, error } = useSWR(
    isAuthenticated ? 'user/me' : null,
    fetcher,
    {
      revalidateOnFocus: false,
      errorRetryInterval: 10000,
    }
  );

  const newGroup = (val) => {
    onGroup(val);
  };

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem(BE_LAST_LOGIN_KEY, JSON.stringify(group));
    }
  }, [group, isAuthenticated]);

  useEffect(() => {
    const userData = user?.data || user;
    if (!userData?.hierarquia?.length) return;
    if (!isGroupInUserHierarchy(group, userData)) {
      const first = userData.hierarquia[0];
      onGroup({ value: first.id, label: first.cliente });
      localStorage.removeItem('dd-last-login');
    }
  }, [user, group]);

  const userData = user?.data || user;

  const values = {
    isAdmin: userData?.usuario?.perfil?.id === 5,
    userTypes: { ictsAdmin: 5 },
    user: {
      ...userData,
      isVisualizador: () => userData?.usuario?.perfil?.id === 1,
      isRevisor: () => userData?.usuario?.perfil?.id === 2,
      isGestor: () => userData?.usuario?.perfil?.id === 3,
      isMaster: () => userData?.usuario?.perfil?.id === 4,
      isAdminICTS: () => userData?.usuario?.perfil?.id === 5,
      isAPI: () => userData?.usuario?.perfil?.id === 6,
      canOpenBatches: () => !!userData?.usuario?.can_open_lots,
      comesFromBecompliance: () => !!userData?.usuario?.comesFromBecompliance,
    },
    group,
    newGroup,
  };

  const { showIdleAlert, onShowIdleAlert, formattedTime } = useIdleLogout();

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', maxWidth: '500px', margin: '0 auto' }}>
        <p style={{ marginBottom: '1rem' }}>Token não encontrado. Acesse via Becompliance.</p>
        <p style={{ fontSize: '0.875rem', color: '#666' }}>
          O cookie de autenticação deve estar definido pelo backend.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Erro ao carregar usuário: {error?.message || 'Erro desconhecido'}</p>
        <button type="button" onClick={() => logout()}>Sair</button>
      </div>
    );
  }

  if (!user || !userData) {
    if (isValidating) {
      return <SimpleLoading size="lg" />;
    }
    logout();
    return null;
  }

  return (
    <meContext.Provider value={values}>
      {children}
      {showIdleAlert && (
        <Modal
          show
          onShow={onShowIdleAlert}
          title={t('modal.idle', { formattedTime })}
          hasActions={false}
          titleOnly
          size="lg"
        />
      )}
    </meContext.Provider>
  );
}

BeMeProvider.propTypes = {
  children: element.isRequired,
};
